import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Tag, Copy, Check, Power, Pencil, X, ChevronRight } from 'lucide-react';
import type { SelfServicePromotion } from '../../types/selfService';
import { normalizePromoCode } from '../../types/selfService';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { ApiError, apiClient } from '../../lib/apiClient';

const PublisherPromotionsPage: React.FC = () => {
  const { slug } = useSingleStationContext();
  const [rows, setRows] = React.useState<SelfServicePromotion[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<SelfServicePromotion | null>(null);
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { rows } = await apiClient.post<{ rows: SelfServicePromotion[] }>(
        '/promotions/list',
        { slug },
      );
      setRows(rows ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const upsert = async (payload: {
    id?: string;
    code: string;
    discountPercent: number;
    startsAt?: string | null;
    endsAt?: string | null;
    maxUses?: number | null;
    active?: boolean;
  }) => {
    try {
      await apiClient.post('/promotions', { slug, ...payload });
      await refresh();
    } catch (e) {
      // The API surfaces UNIQUE-violation as 409 ConflictException with
      // message 'code_in_use'; any other 4xx is a generic Save failed.
      if (e instanceof ApiError && (e.status === 409 || e.message === 'code_in_use')) {
        throw new Error('That code is already used by another promotion.');
      }
      throw e instanceof Error ? e : new Error('Save failed');
    }
  };

  const toggleActive = async (row: SelfServicePromotion) => {
    try {
      await upsert({
        id: row.id,
        code: row.code,
        discountPercent: row.discount_percent,
        startsAt: row.starts_at,
        endsAt: row.ends_at,
        maxUses: row.max_uses,
        active: !row.active,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Toggle failed');
    }
  };

  // Buyer-facing share link with the promo applied. Useful for the publisher
  // to drop into newsletters / social posts.
  const shareUrl = (code: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/${slug}?promo=${code}`;
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl(code));
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="max-w-5xl">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 mb-1">Promotions</h1>
            <p className="text-sm text-slate-600">
              Run a promo by giving advertisers a code (e.g. <code className="px-1 py-0.5 rounded bg-slate-100 font-mono">MEMDAY10</code>) for bonus ad plays at the same price.
              Buyers can apply at checkout, or you can share a link that pre-fills it.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setEditing(null); setEditorOpen(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md whitespace-nowrap"
          >
            <Tag className="w-4 h-4" />
            Add promotion
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss"><X className="w-4 h-4" /></button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <Tag className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">No promotions yet</p>
            <p className="text-sm text-slate-500 mt-1">Create your first promo code to offer a discount on this portal.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 bg-slate-50/60 border-b border-slate-200">
                  <th className="px-5 py-3 font-medium">Code</th>
                  <th className="px-3 py-3 font-medium text-right">Bonus plays</th>
                  <th className="px-3 py-3 font-medium">Window</th>
                  <th className="px-3 py-3 font-medium text-right">Uses</th>
                  <th className="px-3 py-3 font-medium">Share</th>
                  <th className="px-3 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {rows.map((r) => (
                  <tr key={r.id} className={r.active ? '' : 'bg-slate-50/40'}>
                    <td className="px-5 py-3">
                      <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 font-mono text-[12px]">{r.code}</code>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-900 font-medium">+{r.discount_percent}%</td>
                    <td className="px-3 py-3 text-slate-600 text-[12px]">{describeWindow(r)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-slate-600">
                      {r.current_uses}{r.max_uses != null ? ` / ${r.max_uses}` : ''}
                    </td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => handleCopy(r.code)}
                        className="inline-flex items-center gap-1.5 text-[12px] text-slate-600 hover:text-slate-900"
                        title={shareUrl(r.code)}
                      >
                        {copiedCode === r.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        {copiedCode === r.code ? 'Copied' : 'Copy link'}
                      </button>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${badgeFor(r)}`}>
                        {labelFor(r)}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex gap-1 items-center">
                        <button
                          type="button"
                          onClick={() => { setEditing(r); setEditorOpen(true); }}
                          className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(r)}
                          className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                          title={r.active ? 'Disable' : 'Re-enable'}
                        >
                          <Power className="w-3.5 h-3.5" />
                        </button>
                        <Link
                          to={`/admin/${slug}/promotions/${r.id}`}
                          className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                          title="View redemptions"
                        >
                          <ChevronRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editorOpen && (
          <PromotionEditor
            initial={editing}
            onCancel={() => setEditorOpen(false)}
            onSave={async (payload) => {
              await upsert(payload);
              setEditorOpen(false);
            }}
        />
      )}
    </div>
  );
};

// Badge styling reflects the promo's effective state, not just the active
// column — an expired or fully-redeemed promo is "exhausted" so the
// publisher can spot dead codes at a glance.
function effectiveState(r: SelfServicePromotion): 'active' | 'scheduled' | 'expired' | 'exhausted' | 'disabled' {
  if (!r.active) return 'disabled';
  if (r.max_uses != null && r.current_uses >= r.max_uses) return 'exhausted';
  const now = Date.now();
  if (r.starts_at && new Date(r.starts_at).getTime() > now) return 'scheduled';
  if (r.ends_at && new Date(r.ends_at).getTime() < now) return 'expired';
  return 'active';
}

function badgeFor(r: SelfServicePromotion): string {
  switch (effectiveState(r)) {
    case 'active':    return 'bg-emerald-100 text-emerald-800';
    case 'scheduled': return 'bg-sky-100 text-sky-800';
    case 'expired':   return 'bg-slate-200 text-slate-600';
    case 'exhausted': return 'bg-amber-100 text-amber-800';
    case 'disabled':  return 'bg-slate-200 text-slate-700';
  }
}

function labelFor(r: SelfServicePromotion): string {
  switch (effectiveState(r)) {
    case 'active':    return 'Active';
    case 'scheduled': return 'Scheduled';
    case 'expired':   return 'Expired';
    case 'exhausted': return 'Exhausted';
    case 'disabled':  return 'Disabled';
  }
}

function describeWindow(r: SelfServicePromotion): string {
  if (!r.starts_at && !r.ends_at) return 'Always';
  const fmt = (s: string | null) =>
    s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  if (r.starts_at && r.ends_at) return `${fmt(r.starts_at)} – ${fmt(r.ends_at)}`;
  if (r.starts_at) return `From ${fmt(r.starts_at)}`;
  return `Until ${fmt(r.ends_at)}`;
}

const PromotionEditor: React.FC<{
  initial: SelfServicePromotion | null;
  onCancel: () => void;
  onSave: (payload: {
    id?: string;
    code: string;
    discountPercent: number;
    startsAt?: string | null;
    endsAt?: string | null;
    maxUses?: number | null;
    active?: boolean;
  }) => Promise<void>;
}> = ({ initial, onCancel, onSave }) => {
  const [code, setCode] = React.useState(initial?.code ?? '');
  const [discount, setDiscount] = React.useState(initial?.discount_percent?.toString() ?? '10');
  // HTML date inputs want yyyy-mm-dd; strip the time portion of the ISO string.
  const [startsAt, setStartsAt] = React.useState(initial?.starts_at?.slice(0, 10) ?? '');
  const [endsAt, setEndsAt] = React.useState(initial?.ends_at?.slice(0, 10) ?? '');
  const [maxUses, setMaxUses] = React.useState(initial?.max_uses?.toString() ?? '');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedCode = normalizePromoCode(code);
    if (normalizedCode.length < 3 || normalizedCode.length > 32) {
      setError('Code must be 3–32 chars'); return;
    }
    const discountNum = Number(discount);
    if (!Number.isFinite(discountNum) || discountNum <= 0 || discountNum > 100) {
      setError('Discount must be > 0 and ≤ 100'); return;
    }
    const maxUsesNum = maxUses.trim() === '' ? null : Number(maxUses);
    if (maxUsesNum != null && (!Number.isFinite(maxUsesNum) || maxUsesNum <= 0)) {
      setError('Max uses must be a positive number'); return;
    }
    if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) {
      setError('End date must be after start date'); return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSave({
        id: initial?.id,
        code: normalizedCode,
        discountPercent: discountNum,
        startsAt: startsAt ? new Date(startsAt + 'T00:00:00').toISOString() : null,
        endsAt: endsAt ? new Date(endsAt + 'T23:59:59').toISOString() : null,
        maxUses: maxUsesNum,
        active: initial?.active ?? true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !busy && onCancel()} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-1">
          {initial ? 'Edit promotion' : 'Add promotion'}
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Pick a code, the % bonus plays buyers get, and an optional window or usage cap.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Code</span>
            <input
              type="text"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 32))}
              placeholder="MEMDAY10"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm font-mono tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Bonus plays %</span>
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Same price for the buyer, this many extra ad plays on top.
            </p>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Starts (optional)</span>
              <input
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
                className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Ends (optional)</span>
              <input
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Max total uses (optional)</span>
            <input
              type="number"
              min={1}
              step={1}
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Unlimited"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">Each buyer can still only redeem this code once.</p>
          </label>

          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {initial ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default PublisherPromotionsPage;
